import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { isEqual } from "date-fns";
import { AttributeType } from "@evergis/api";

import { DateFilterContext } from "../context";

import { getNextTick, getTick } from "../uitls";
import { useTableFilters } from "../../../hooks/table/useTableFilters";
import { updateDateFilterConditions } from "../../../ducks/dateFilter/dateFilter";
import { getComparisonOperatorOptions } from "../../../components/LayerFilter/utils/getComparisonOperatorOptions";

import { Layer } from "../reducers";

const DEFAULT_DEBOUNCE_TIME = 100;

const createDateConditionFormat = (value: Date) => `#'${value.toISOString()}'`;

export const createCondition = (
  layer: Layer,
  firstTick: Date,
  secondTick: Date,
): { name: string; condition: string; isEnabled: boolean } => ({
  isEnabled: firstTick ? layer.selected : false,
  name: layer.id,
  condition: firstTick
    ? `${layer.attribute} >= ${createDateConditionFormat(firstTick)}${
        secondTick && !isEqual(firstTick, secondTick)
          ? ` && ${layer.attribute} < ${createDateConditionFormat(secondTick)}`
          : ""
      }`
    : "",
});

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const useSetRangeCondition = () => {
  const dispatch = useDispatch();
  const { layers } = useContext(DateFilterContext);
  const [tableFilters, { setTableFilters }] = useTableFilters();

  return useCallback(
    (firstTick, secondTick) => {
      setTableFilters(
        layers
          .filter(({ selected }) => selected)
          .reduce(
            (acc, curr) => {
              return {
                ...acc,
                [curr.id]: {
                  ...acc?.[curr.id],
                  [curr.attribute]: {
                    dataType: AttributeType.DateTime,
                    value: [firstTick, secondTick],
                    filterType: getComparisonOperatorOptions(AttributeType.DateTime).find(
                      item => item.value === "Between",
                    ),
                  },
                },
              };
            },
            { ...tableFilters },
          ),
      );

      const conditions = layers
        .filter(({ selected }) => selected)
        .map(layer => createCondition(layer, firstTick, secondTick));

      dispatch(updateDateFilterConditions(conditions));
    },
    [dispatch, layers, tableFilters, setTableFilters],
  );
};

const useStepHandler = (step, initStep) => {
  const { bandScale, setSelectedTick, selectedTick } = useContext(DateFilterContext);
  const setRangeCondition = useSetRangeCondition();

  return useCallback(() => {
    const domain = bandScale.domain();
    const tickIndex = domain.findIndex(date => date.getTime() === (selectedTick && selectedTick.getTime()));
    const selectedTickIndex = tickIndex >= 0 ? tickIndex + step : initStep;
    const nextSelectedTick = domain[selectedTickIndex];
    const secondNextSelectedTick = domain[selectedTickIndex + 1];

    setSelectedTick(nextSelectedTick);

    setRangeCondition(nextSelectedTick, secondNextSelectedTick);
  }, [bandScale, initStep, selectedTick, setRangeCondition, setSelectedTick, step]);
};

export const useNextStepHandler = () => {
  return useStepHandler(1, 0);
};

export const usePrevStepHandler = () => {
  const { bandScale } = useContext(DateFilterContext);
  const ticks = bandScale.domain();

  return useStepHandler(-1, ticks.length - 1);
};

export const useAxisHandler = () => {
  const setRangeCondition = useSetRangeCondition();
  const { setSelectedTick, commonTickScale, rangeDate, durationInterval, scrollLeft, selectedTick } =
    useContext(DateFilterContext);
  const scrollLeftRef: { current: number } = useRef(scrollLeft);
  const conditionTick = useDebounce(selectedTick, DEFAULT_DEBOUNCE_TIME);

  scrollLeftRef.current = scrollLeft;
  let handleMouseMove;

  const mouseMoveHandleCreator = padding => event => {
    const clientX = event.clientX - padding + scrollLeftRef.current;
    const tickIndex = Math.floor(commonTickScale(clientX));

    const firstTick = getTick(rangeDate, durationInterval, tickIndex);

    setSelectedTick(firstTick);
  };

  const handleMouseUp = () => {
    window.removeEventListener("mouseup", handleMouseUp);
    window.removeEventListener("mousemove", handleMouseMove);
  };

  const mouseDownHandler = (leftPadding: number, event) => {
    handleMouseMove = mouseMoveHandleCreator(leftPadding);
    handleMouseMove(event);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    const secondTick = getNextTick(rangeDate, durationInterval, conditionTick);

    setRangeCondition(conditionTick, secondTick);
  }, [conditionTick, durationInterval, rangeDate]);

  return mouseDownHandler;
};

const LEFT_KEY = 37;
const RIGHT_KEY = 39;

export const useKeyboardAxisControl = () => {
  const nextHandler = useNextStepHandler();
  const prevHandler = usePrevStepHandler();

  const keyBoardHandler = useCallback(
    event => {
      if (event.keyCode === RIGHT_KEY) {
        event.preventDefault();
        nextHandler();
      }
      if (event.keyCode === LEFT_KEY) {
        event.preventDefault();
        prevHandler();
      }
    },
    [nextHandler, prevHandler],
  );

  useEffect(() => {
    window.addEventListener("keydown", keyBoardHandler);

    return () => {
      window.removeEventListener("keydown", keyBoardHandler);
    };
  }, [keyBoardHandler]);
};

const PLAY_INTERVAL = 700;

export const usePlayTicks = () => {
  const interval: { current?: NodeJS.Timeout } = useRef(null);
  const next = useNextStepHandler();
  const nextRef: { current: () => void } = useRef(next);

  const play = useCallback(() => {
    interval.current = setInterval(() => {
      nextRef.current();
    }, PLAY_INTERVAL);
  }, []);

  useEffect(() => {
    nextRef.current = next;
  }, [next]);

  const pause = useCallback(() => {
    clearInterval(interval.current as NodeJS.Timeout);
  }, [interval]);

  return [play, pause];
};
